import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  Container
} from '@mui/material';
import { getallbooksdata, addtoCart } from '../../services/Apis';
import { useNavigate } from 'react-router-dom';
import { useFilter } from '../../context/FilterContext';

const UpcomingBooks = () => {
  const [todayOfferBooks, setTodayOfferBooks] = useState([]);
  const { token, isLoggedIn } = useFilter();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleBuyNow = async (book) => {
    if (isLoggedIn && token) {
      try {
        navigate('/checkout', { state: book });
      } catch (error) {
        console.log('Failed to proceed to checkout.');
      }
    } else {
      navigate('/login');
    }
  };

  const handleAddToCart = async (bookId) => {
    if (isLoggedIn && token) {
      try {
        await addtoCart({ bookId, quantity: 1 });
        navigate('/cart');
      } catch (error) {
        alert('Failed to add to cart. Please login or try again.');
      }
    } else {
      navigate('/login');
    }
  };

  useEffect(() => {
    const fetchBooks = async () => {
      const books = await getallbooksdata();
      setTodayOfferBooks(books.todayOffers || []);
      setLoading(false);
    };
    fetchBooks();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ pr: '0px !important', pl: '10px !important' }}>
      <Box sx={{ px: 3, py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              fontFamily: 'Poppins',
              color: '#CB4422',
              borderLeft: '4px solid #000000',
              paddingLeft: 1
            }}
          >
            Today's Offer Books
          </Typography>
        </Box>

        {/* Loader */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <span className="red-dot" />
              <span className="red-dot" style={{ animationDelay: '0.2s' }} />
              <span className="red-dot" style={{ animationDelay: '0.4s' }} />
            </Box>
            <style>
              {`
                .red-dot {
                  display: inline-block;
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  background: #CB4422;
                  margin: 0 4px;
                  animation: bounce 1s infinite;
                }
                @keyframes bounce {
                  0%, 80%, 100% { transform: scale(1); opacity: 1; }
                  40% { transform: scale(1.4); opacity: 0.7; }
                }
              `}
            </style>
          </Box>
        ) : todayOfferBooks.length === 0 ? (
          // Coming Soon Placeholder
         <Box sx={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:220}}>
         <Typography variant="h6" sx={{ color: '#666', fontWeight: 500, mb: 1 }}>
         No Books Available
         </Typography>
         </Box>
        ) : ( 
            // Horizontal Scroll Section with hidden scrollbar
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 3,
              paddingBottom: 2,
              scrollBehavior: 'smooth',
              // Hide scrollbar for Chrome, Safari and Opera
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              // Hide scrollbar for IE, Edge and Firefox
              msOverflowStyle: 'none',  // IE and Edge
              scrollbarWidth: 'none',  // Firefox
            }}
          >
            {todayOfferBooks.map((book, index) => (
              <Box
                key={index}
                sx={{
                  width: 260, // Fixed width for all cards
                  flexShrink: 0,
                  height: '100%'
                }}
              >
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  {/* Book Image */}
                  <Box 
                    sx={{ 
                      width: '100%',
                      height: 250, // Fixed height for image
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <CardMedia
                      component="img"
                      onClick={() => navigate(`/book-overview/${book.id}`)}
                      image={book?.bookImages[0]?.imageUrl}
                      alt={book.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        backgroundColor: '#f5f5f5',
                        p: 2,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)'
                        }
                      }}
                    />
                    <IconButton
                      disableFocusRipple
                      disableRipple
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: '#000',
                        '&:hover': { 
                          bgcolor: '#FFB300',
                          color: '#000'
                        },
                        width: 32,
                        height: 32
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(book.id);
                      }}
                    >
                      <i className="fa-solid fa-cart-shopping" style={{ fontSize: '14px' }}></i>
                    </IconButton>
                  </Box>

                  {/* Book Title, Discount, Button */}
                  <CardContent sx={{ 
                    p: 2,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <Box>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 500,
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minHeight: '3em', // 2 lines of text
                          lineHeight: '1.5em'
                        }}
                      >
                        {book.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="error" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {book.discountOffer}% Off Discount on MRP
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={() => handleBuyNow(book)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                        borderRadius: '4px',
                        py: 0.5,
                        mt: 1,
                        '&:hover': { 
                          backgroundColor: 'primary.dark',
                          boxShadow: 'none' 
                        }
                      }}
                    >
                      Buy Now
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default UpcomingBooks;
